<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore } from '@/stores/CartStore'

import Logo from '../vectors/Logo.vue';
import Button from '../../mixit-ui/components/button/Button.vue';
import InputSearch from '../../mixit-ui/components/form/InputSearch.vue';
import Flag from '../vectors/Flag.vue';
import Badge from '../../mixit-ui/components/misc/Badge.vue';
import DropdownLanguageSelect from '../dropdowns/DropdownLanguageSelect.vue';
import SearchBox from '../dropdowns/SearchBox.vue'
import MoreMenu from '../dropdowns/DropdownMoreMenu.vue';
import DropdownApp from '../dropdowns/DropdownApp.vue';
import DropdownNotifications, { type Notification } from '../dropdowns/DropdownNotifications.vue';
import DropdownCart from '../dropdowns/DropdownCart.vue';
import DropdownProfile from '../dropdowns/DropdownProfile.vue';
import { searchService, type SearchProduct, type SearchCategory, type SearchArticle } from '@/services-new/searchService';
import { notificationService } from '@/services-new/notificationService';
import { categoryService, type ProductCategory } from '@/services-new/categoryService';

const searchRef = ref('');

// --- search state (lives here so it survives SearchBox unmount) ---
const searchProducts = ref<SearchProduct[]>([]);
const searchCategories = ref<SearchCategory[]>([]);
const searchArticles = ref<SearchArticle[]>([]);
const searchTopSearches = ref<string[]>([]);
const crossSellProducts = ref<SearchProduct[]>([]);
const idleCategories = ref<ProductCategory[]>([]);
const isSearchActive = computed(() => searchRef.value.trim().length >= 2);
const isSearchLoading = ref(false);

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

watch(searchRef, (q) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  if (!q || q.trim().length < 2) {
    searchProducts.value = [];
    searchCategories.value = [];
    searchArticles.value = [];
    isSearchLoading.value = false;
    return;
  }
  isSearchLoading.value = true;
  searchDebounceTimer = setTimeout(async () => {
    const [result, articles] = await Promise.all([
      searchService.search(q),
      searchService.searchArticles(q),
    ]);
    searchProducts.value = result.products;
    searchCategories.value = result.categories;
    searchArticles.value = articles;
    isSearchLoading.value = false;
  }, 300);
});

const cartStore = useCartStore()
const cartTotalPrice = computed(() => cartStore.getCartPrice)
const cartItemCount = computed(() => cartStore.getTotalItemsInCart)

const notifications = ref<Notification[]>([]);
const notificationsUnreadCount = computed(() => notifications.value.filter(n => !n.isRead).length);
const currentCountry = 'cs';


const searchIsOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const headerRef = ref<HTMLElement | null>(null)
const isMobile = ref(window.innerWidth < 1184)
const router = useRouter()
const mobileSearchMaxHeight = ref('85dvh')

function updateMobileSearchHeight() {
  if (headerRef.value) {
    const bottom = headerRef.value.getBoundingClientRect().bottom
    mobileSearchMaxHeight.value = `calc(100dvh - ${bottom}px)`
  }
}

function checkMobile() {
  isMobile.value = window.innerWidth < 1184
  if (isMobile.value) updateMobileSearchHeight()
}

function onClickOutside(e: MouseEvent) {
  const popper = document.querySelector('.v-popper__popper')
  if (
    !dropdownRef.value?.contains(e.target as Node) &&
    !popper?.contains(e.target as Node)
  ) {
    searchIsOpen.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', onClickOutside)
  window.addEventListener('resize', checkMobile)
  window.addEventListener('scroll', updateMobileSearchHeight)
  updateMobileSearchHeight()
  const [topSearches, crossSell, allCategories] = await Promise.all([
    searchService.loadTopSearches(),
    searchService.loadCrossSellProducts(),
    categoryService.loadProductsCategories(),
  ])
  searchTopSearches.value = topSearches
  crossSellProducts.value = crossSell
  idleCategories.value = allCategories.slice(0, 6)

  notificationService.load().then(rawNotifications => {
    notifications.value = rawNotifications.map(n => ({
      id: n.id,
      type: n.notificationType as Notification['type'],
      title: n.title,
      description: n.content,
      time: n.createdAt,
      isRead: n.isRead,
    }))
  }).catch(() => {})
})
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
  window.removeEventListener('resize', checkMobile)
  window.removeEventListener('scroll', updateMobileSearchHeight)
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})
</script>

<template>
  <header
    ref="headerRef"
    class="
      bg-brown-600
      px-4 sm:px-10 pt-5 2lg:pt-0 pb-4 2lg:pb-0
      lg:gap-x-12 gap-y-5
      flex flex-row items-center flex-wrap
      z-30 sticky top-0 h-auto 2lg:h-18
    "
  >
    <Logo class="w-25 sm:w-33 h-auto 2lg:mr-0 mr-auto" />
    <div class="hidden lg:flex flex-row gap-6 items-center">
      <!-- Desktop App menu -->
      <VDropdown
        distance="26"
        :arrow-padding="0"
        :triggers="['click', 'hover', 'focus']"
        :popper-triggers="['click', 'hover', 'focus']"
        strategy="fixed"
        popper-class="no-arrow no-style-popper p-0"
        :auto-hide="false"
        :no-auto-focus="true"
      >
        <Button
          icon="mi-smartphone text-yellow-400"
          label="Aplikace"
          variant="text"
          severity="white"
          size="md"
        />
        <template #popper>
          <DropdownApp />
        </template>
      </VDropdown>
      <!-- Desktop menu -->
      <VDropdown
        distance="26"
        :arrow-padding="0"
        :triggers="['click', 'hover', 'focus']"
        :popper-triggers="['click', 'hover', 'focus']"
        strategy="fixed"
        popper-class="no-arrow no-style-popper p-0"
        :auto-hide="false"
        :no-auto-focus="true"
      >
        <Button
          icon="mi-menu text-yellow-400"
          label="Více"
          variant="text"
          severity="white"
          size="md"
        />
        <template #popper>
          <MoreMenu />
        </template>
      </VDropdown>
    </div>
    <!-- Search box -->
    <div
      ref="dropdownRef"
      class="w-full order-last 2lg:order-none 2lg:flex-1 flex items-center gap-2"
    >
      <VDropdown
        class="flex-1"
        :arrow-padding="0"
        :popper-class="isMobile ? 'no-arrow no-style-popper search-bottom-sheet' : 'no-arrow no-style-popper'"
        :shown="searchIsOpen"
        :auto-hide="false"
        :triggers="[]"
        :no-auto-focus="true"
        :distance="isMobile ? 0 : 12"
        :backdrop="!isMobile"
        strategy="fixed"
        :positioning-disabled="isMobile"
        :transition="isMobile ? { name: 'search-slide-up' } : undefined"
      >
        <InputSearch
          id="searchHeader"
          v-model="searchRef"
          wrapper-id="searchHeaderWrap"
          label="Jaké zobání hledáte?"
          @focus="searchIsOpen = true"
          @click="searchIsOpen = true"
        />
        <template #popper>
          <SearchBox
            :is-open="searchIsOpen"
            :is-mobile="isMobile"
            :mobile-max-height="mobileSearchMaxHeight"
            :products="searchProducts"
            :categories="searchCategories"
            :articles="searchArticles"
            :top-searches="searchTopSearches"
            :cross-sell-products="crossSellProducts"
            :idle-categories="idleCategories"
            :is-search-active="isSearchActive"
            :is-search-loading="isSearchLoading"
            :query="searchRef"
            @on-hide="searchIsOpen"
          />
        </template>
      </VDropdown>
      <button
        v-if="searchIsOpen"
        class="flex 2lg:hidden justify-center items-center"
        @click="searchIsOpen = false;"
      >
        <i class="mi-close text-3xl text-yellow-200" />
      </button>
    </div>
    <div class="flex flex-row items-center gap-6 sm:gap-10">
      <div class="flex flex-row items-center gap-4 sm:gap-6">
        <!-- Dropdown locale menu -->
        <VDropdown
          distance="23"
          :arrow-padding="0"
          :triggers="['click', 'hover', 'focus']"
          :popper-triggers="['click', 'hover', 'focus']"
          strategy="fixed"
          popper-class="no-arrow no-style-popper p-0"
          class="sm:flex hidden"
        >
          <button
            type="button"
            name="Country select"
            class="block"
          >
            <Flag
              :country="currentCountry"
              class="w-6.5 h-auto"
            />
          </button>
          <template #popper>
            <DropdownLanguageSelect />
          </template>
        </VDropdown>
        <!-- Dropdown notifications -->
        <VDropdown
          distance="26"
          :arrow-padding="0"
          :triggers="['click', 'hover', 'focus']"
          :popper-triggers="['click', 'hover', 'focus']"
          strategy="fixed"
          popper-class="no-arrow no-style-popper p-0"
        >
          <button class="flex flex-row gap-1 items-center cursor-pointer relative">
            <i class="mi-bell text-2xl sm:text-lg text-yellow-400" />
            <Badge
              v-if="notificationsUnreadCount > 0"
              :label="String(notificationsUnreadCount)"
              size="sm"
              severity="danger"
              class="sm:relative absolute bottom-3.5 left-3 sm:left-0 sm:bottom-0"
            />
          </button>
          <template #popper>
            <DropdownNotifications :notifications="notifications" />
          </template>
        </VDropdown>
        <VDropdown
          distance="26"
          :arrow-padding="0"
          :triggers="['click', 'hover', 'focus']"
          :popper-triggers="['click', 'hover', 'focus']"
          strategy="fixed"
          popper-class="no-arrow no-style-popper p-0"
        >
          <Button
            label="Můj účet"
            label-class="sm:flex hidden"
            variant="text"
            severity="white"
            size="md"
          >
            <template #icon>
              <i class="mi-user-fill text-yellow-400 text-2xl sm:text-lg" />
            </template>
          </Button>
          <template #popper>
            <DropdownProfile />
          </template>
        </VDropdown>
        <!-- Cart -->
        <VDropdown
          distance="26"
          :arrow-padding="0"
          :triggers="isMobile ? [] : ['click', 'hover', 'focus']"
          :popper-triggers="isMobile ? [] : ['click', 'hover', 'focus']"
          strategy="fixed"
          popper-class="no-arrow no-style-popper p-0"
          :auto-hide="false"
          :no-auto-focus="true"
        >
          <div
            class="flex flex-row gap-1 relative items-center lg:w-28"
            @click="isMobile && router.push('/cart')"
          >
            <Button
              :label="cartTotalPrice"
              label-class="sm:flex hidden"
              variant="text"
              severity="white"
              size="md"
            >
              <template #icon>
                <i class="mi-cart-fill text-yellow-400 text-2xl sm:text-lg" />
              </template>
            </Button>
            <Badge
              v-if="cartItemCount > 0"
              :label="String(cartItemCount)"
              size="sm"
              severity="danger"
              class="sm:relative absolute bottom-3.5 left-3 sm:left-0 sm:bottom-0"
            />
          </div>
          <template #popper>
            <DropdownCart
            />
          </template>
        </VDropdown>
      </div>
      <!-- Mobile nav menu -->
      <button class="lg:hidden flex flex-col gap-0" @click="router.push({ name: 'menuBeta' })">
        <i class="mi-menu text-yellow-400 text-2xl leading-0" />
        <strong class="text-2xs text-white font-roboto-slab leading-2">Menu</strong>
      </button>
    </div>
  </header>
</template>
